// @ts-nocheck
/**
 * @file Email_Input.tsx
 * @description Premium, responsive Email Input adapted from sk-voice-fe.
 */

import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, Animated, Platform } from 'react-native';
import { Colors } from '../../theme/tokens';
import { emailValidation } from '../../utils/Validations/email';

interface EmailInputProps {
  onValueChange?: (email: string, isValid: boolean) => void;
  onBlur?: (e?: any) => void;
  defaultValue?: string;
  isReadOnly?: boolean;
  value?: string;
  error?: string;
  touched?: boolean;
  onSubmitEditing?: () => void;
  returnKeyType?: "done" | "go" | "next" | "send" | "search";
  /** Compact sizing for dense surfaces (e.g. the in-modal Add-customer card). */
  dense?: boolean;
  /** Show a red required asterisk next to the label. */
  required?: boolean;
  /** Override the default "Email Address" label text. */
  label?: string;
}

const Email_Input: React.FC<EmailInputProps> = ({
  onValueChange,
  onBlur,
  defaultValue = '',
  isReadOnly = false,
  value,
  error: externalError,
  touched,
  onSubmitEditing,
  returnKeyType,
  dense = false,
  required = false,
  label,
}) => {
  const [internalEmail, setInternalEmail] = useState<string>(defaultValue);
  const [internalError, setInternalError] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [wasBlurred, setWasBlurred] = useState<boolean>(false);
  const [shakeAnimation] = useState(new Animated.Value(0));

  const email = value ?? internalEmail;
  const error = externalError ?? internalError;
  const isErrorVisible = touched !== undefined ? (touched && !!error) : (wasBlurred && !!error);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof globalThis !== 'undefined') {
      const doc = (globalThis as any).document;
      if (!doc) return;
      const style = doc.createElement('style');
      style.textContent = `
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset !important;
          box-shadow: 0 0 0 1000px #FFFFFF inset !important;
          -webkit-text-fill-color: #1E293B !important;
          caret-color: #1E293B !important;
          transition: background-color 9999s ease-in-out 0s !important;
        }
      `;
      doc.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    setInternalEmail(defaultValue);
  }, [defaultValue]);

  // Validate whenever the controlled value changes (handles programmatic fills)
  useEffect(() => {
    if (value !== undefined && value.length > 0) {
      validateEmail(value);
    }
  }, [value]);

  const validateEmail = async (val: string) => {
    try {
      await emailValidation.validate(val);
      setInternalError('');
      if (onValueChange) onValueChange(val, true);
    } catch (err: any) {
      setInternalError(err.message);
      if (onValueChange) onValueChange(val, false);
    }
  };

  const triggerShake = () => {
    Animated.sequence([
      Animated.timing(shakeAnimation, { toValue: 8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 0, duration: 50, useNativeDriver: true }),
    ]).start();
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);
    setWasBlurred(true);
    validateEmail(email);
    if (error || email.length === 0) triggerShake();
    if (onBlur) onBlur(e);
  };

  useEffect(() => {
    validateEmail(email);
  }, [email]);

  const getWrapperStyle = () => {
    if (isReadOnly) return styles.inputReadOnly;
    if (isFocused) return styles.inputFocused;
    if (isErrorVisible) return styles.inputError;
    return styles.inputDefault;
  };

  let textColor = '#1E293B';
  if (isReadOnly) textColor = '#64748B';
  else if (error && email.length > 0) textColor = '#EF4444';

  return (
    <View style={[styles.container, dense && styles.containerDense]}>
      <View style={[styles.labelRow, dense && styles.labelRowDense]}>
        <Text style={[styles.label, dense && styles.labelDense]}>
          {label ?? 'Email Address'}
          {required && <Text style={styles.req}> *</Text>}
        </Text>
        {isReadOnly && (
          <View style={styles.readOnlyBadge}>
            <Text style={styles.readOnlyText}>SECURED</Text>
          </View>
        )}
      </View>

      <Animated.View
        style={[
          styles.inputWrapper,
          dense && styles.inputWrapperDense,
          { transform: [{ translateX: shakeAnimation }] },
          getWrapperStyle(),
        ]}
      >
        <TextInput
          value={email}
          onChangeText={(text) => {
            if (value === undefined) setInternalEmail(text);
            if (onValueChange) onValueChange(text, true);
            if (internalError) validateEmail(text);
          }}
          onFocus={() => { if (!isReadOnly) setIsFocused(true); }}
          onBlur={handleBlur}
          editable={!isReadOnly}
          placeholder={isReadOnly ? "" : "Enter Email Address"}
          placeholderTextColor={Colors.ink4}
          style={[
            styles.input,
            dense && styles.inputDense,
            { color: textColor },
            Platform.select({
              android: { fontFamily: '', fontWeight: undefined },
              web: { outlineStyle: 'none' } as any,
            }),
          ]}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="off"
          underlineColorAndroid="transparent"
          {...(Platform.OS === 'web' ? { autoComplete: 'new-password' } as any : {})}
          onSubmitEditing={onSubmitEditing}
          returnKeyType={returnKeyType}
        />
      </Animated.View>

      {isErrorVisible && <Text style={[styles.errorLabel, dense && styles.errorLabelDense]}>{error}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { marginVertical: 12, width: '100%' },
  containerDense: { marginVertical: 0, width: '100%' },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  labelRowDense: { marginBottom: 6 },
  label: { fontSize: 12, fontWeight: '800', color: Colors.ink3, textTransform: 'uppercase', letterSpacing: 1.5 },
  labelDense: { fontSize: 11, letterSpacing: 1 },
  req: { color: Colors.danger, fontWeight: '900', fontSize: 15 },
  readOnlyBadge: {
    backgroundColor: Colors.paper2,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Colors.line,
  },
  readOnlyText: { fontSize: 9, fontWeight: '900', color: Colors.ink3, letterSpacing: 1 },
  inputWrapper: {
    height: 42,
    borderRadius: 2,
    borderWidth: 1.5,
    backgroundColor: Colors.card,
    paddingHorizontal: 16,
    justifyContent: 'center',
  },
  inputWrapperDense: {
    height: 34,
    borderRadius: 10,
    borderWidth: 1.25,
    paddingHorizontal: 12,
  },
  inputDefault: { borderColor: Colors.lineStrong },
  inputFocused: { borderColor: Colors.accent, backgroundColor: Colors.card, borderWidth: 2 },
  inputError: { borderColor: Colors.danger, backgroundColor: Colors.dangerSoft },
  inputReadOnly: { borderColor: Colors.line, backgroundColor: Colors.paper2, opacity: 0.8 },
  input: {
    fontSize: 15,
    fontWeight: '500',
    height: '100%',
    width: '100%',
    backgroundColor: 'transparent',
    color: Colors.ink,
  },
  inputDense: { fontSize: 13.5 },
  errorLabel: { color: Colors.danger, fontSize: 14, fontWeight: '600', marginTop: 6, marginLeft: 4 },
  errorLabelDense: { color: '#EF4444', fontSize: 12, fontWeight: '600', marginTop: 6, marginLeft: 4 },
});

export default Email_Input;
